import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import { LOCAL_STORAGE_KEY } from '@/utils/constant';
import { BLACK_LANGUAGES, WHITE_LANGUAGES } from '@/utils/langUtils';
import { getCountryLang } from '@/utils/langUtils';
import { FlagImg } from '@common/external/icons8';
import Modal from '@common/modal';
import Radio from '@common/radio';
import RadioGroup from '@common/radiogroup';

const StyledRadioOption = styled.span`
  display: flex;
  align-items: center;
  @media (max-width: 768px) {
    gap: 2px;
  }
  @media not (max-width: 768px) {
    flex-direction: column;
  }
  ${(props) =>
    props.$current &&
    `
    &::after {
      content: '⭐️';
      @media not (max-width: 768px) {
        padding-top: 2px;
      }
    }
  `}
`;

const StyledRadio = styled.div`
  margin-bottom: 2rem;
  @media not (max-width: 768px) {
    width: 100%;
  }
  & fieldset {
    display: flex;
    @media (max-width: 768px) {
      gap: 1rem;
      flex-direction: column;
      align-items: start;
    }
  }
  & label {
    @media not (max-width: 768px) {
      word-break: keep-all;
      flex-direction: column;
    }
  }
`;

const StyledInfo = styled.span`
  color: gray;
  height: 70px;
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 20px;
  @media (max-width: 768px) {
    gap: 10px;
  }
  & div.from,
  & div.to {
    flex-grow: 1;
    flex-basis: 0;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  & div.from {
    justify-content: end;
  }
`;

const LanguageChangeButtonModal = (props) => {
  const { t, i18n } = useTranslation();
  const { language: curLang, resolvedLanguage, changeLanguage } = i18n;
  const [isShow, setIsShow] = useState(false);
  const [radioValue, setRadioValue] = useState(curLang);
  const [currentLang, setCurrentLang] = useState('');
  const [nextLang, setNextLang] = useState('');

  useEffect(() => {
    const savedLanguage = localStorage.getItem(LOCAL_STORAGE_KEY.LANGUAGE);
    setRadioValue(savedLanguage ?? curLang);
  }, []);

  useEffect(() => {
    setCurrentLang(getCountryLang(t, curLang, curLang));
    setNextLang(getCountryLang(t, radioValue, curLang));
  }, [curLang, radioValue]);

  const radioOption = (lang) => {
    const currentLangName = getCountryLang(t, lang);
    const isCurrent = lang === resolvedLanguage;
    const uniqueLangName = `(${getCountryLang(t, lang, lang)})`;
    return (
      <StyledRadioOption $current={isCurrent}>
        <span>{currentLangName}</span>
        {!isCurrent && <span>{uniqueLangName}</span>}
      </StyledRadioOption>
    );
  };

  const onApply = () => {
    changeLanguage(radioValue);
    localStorage.setItem(LOCAL_STORAGE_KEY.LANGUAGE, radioValue);
    setIsShow(false);
  };

  const onCancel = () => {
    setRadioValue(curLang);
    setIsShow(false);
  };

  return (
    <>
      <button className="transparent" onClick={() => setIsShow(true)}>
        {props.children}
      </button>
      {isShow && (
        <Modal
          title={t('setLanguage')}
          applyProps={{ disabled: curLang === radioValue, onClick: onApply }}
          cancelProps={{ onClick: onCancel }}
          style={{ width: '100%' }}
        >
          <StyledInfo>
            {radioValue === curLang ? (
              t('please_select_language')
            ) : (
              <>
                <div className="from">
                  <b>{currentLang}</b>
                  {FlagImg(curLang)}
                </div>
                <div>👉🏻</div>
                <div className="to">
                  <b>{nextLang}</b>
                  {FlagImg(radioValue)}
                </div>
              </>
            )}
          </StyledInfo>
          <StyledRadio>
            <RadioGroup value={radioValue} onChange={setRadioValue}>
              {WHITE_LANGUAGES.map((lang, i) => (
                <Radio key={`suppotedLanguage${i}`} value={lang}>
                  {radioOption(lang)}
                </Radio>
              ))}
              {BLACK_LANGUAGES.map((lang, i) => (
                <Radio key={`unsuppotedLanguage${i}`} value={lang}>
                  {radioOption(lang)}
                </Radio>
              ))}
            </RadioGroup>
          </StyledRadio>
        </Modal>
      )}
    </>
  );
};

export default LanguageChangeButtonModal;
